import type { CSSProperties } from 'react';
import { scaleOf, shareOnScale, type ValueScale } from '../ColumnChart/chart-scale.ts';
import { centerOf } from '../Chart/BucketLabels.tsx';
import { ChartFigure, PlotFrame } from '../Chart/ChartFrame.tsx';
import { ChartTooltip, type TooltipRow } from '../Chart/ChartTooltip.tsx';
import { bucketsOf, hasValues, type ChartSeries, type SeriesKey } from '../Chart/chart-series.ts';
import { useBucketFocus } from '../Chart/use-bucket-focus.ts';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import { seriesColorOf } from '../../tokens/chart.ts';
import styles from './StackedBarChart.module.css';

type BarArrangement = 'stacked' | 'grouped';

export type StackedBarChartProps = {
  label: string;
  series: readonly ChartSeries[];
  arrangement: BarArrangement;
  formatTick: (value: number) => string;
  emptyTitle: string;
  loading?: boolean;
};

type Segment = {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly display: string;
  readonly color: string;
};

const positive = (value: number): number => Math.max(value, 0);

const segmentsAt = (series: readonly ChartSeries[], index: number): readonly Segment[] =>
  series.flatMap((entry, order) => {
    const point = entry.points[index];
    return point === undefined
      ? []
      : [
          {
            key: entry.key,
            label: entry.label,
            value: positive(point.value),
            display: point.display,
            color: seriesColorOf(entry.key, order),
          },
        ];
  });

const totalOf = (segments: readonly Segment[]): number =>
  segments.reduce((sum, segment) => sum + segment.value, 0);

const scaleFor = (series: readonly ChartSeries[], arrangement: BarArrangement): ValueScale => {
  const buckets = bucketsOf(series);
  const heights = buckets.flatMap((_, index) => {
    const segments = segmentsAt(series, index);
    return arrangement === 'stacked' ? [totalOf(segments)] : segments.map((segment) => segment.value);
  });
  return scaleOf(heights);
};

const heightStyle = (share: number, color?: string): CSSProperties =>
  ({ '--bar-share': String(share), ...(color === undefined ? {} : { background: color }) }) as CSSProperties;

const Stack = ({ segments, scale }: { segments: readonly Segment[]; scale: ValueScale }) => {
  const total = totalOf(segments);
  return (
    <span className={styles['stack']} style={heightStyle(shareOnScale(scale, total))}>
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <span
            key={segment.key}
            className={styles['segment']}
            style={{ flexGrow: segment.value, background: segment.color }}
          />
        ))}
    </span>
  );
};

const Group = ({ segments, scale }: { segments: readonly Segment[]; scale: ValueScale }) => (
  <span className={styles['group']}>
    {segments.map((segment) => (
      <span
        key={segment.key}
        className={styles['bar']}
        style={heightStyle(shareOnScale(scale, segment.value), segment.color)}
      />
    ))}
  </span>
);

const keysOf = (series: readonly ChartSeries[]): readonly SeriesKey[] =>
  series.map((entry, index) => ({
    key: entry.key,
    label: entry.label,
    color: seriesColorOf(entry.key, index),
    mark: 'rect',
  }));

const tooltipRowsOf = (segments: readonly Segment[]): readonly TooltipRow[] =>
  segments.map(({ key, label, display, color }) => ({ key, label, display, color }));

const Plot = ({
  label,
  series,
  arrangement,
  formatTick,
}: Omit<StackedBarChartProps, 'loading' | 'emptyTitle'>) => {
  const scale = scaleFor(series, arrangement);
  const buckets = bucketsOf(series);
  const focus = useBucketFocus(buckets.length, 'centered');
  const Bars = arrangement === 'stacked' ? Stack : Group;
  return (
    <PlotFrame
      label={label}
      scale={scale}
      formatTick={formatTick}
      buckets={buckets}
      placeAt={centerOf}
      plotProps={focus.plotProps}
    >
      <div className={styles['buckets']}>
        {buckets.map((bucket, index) => (
          <span
            key={bucket.key}
            className={styles['bucket']}
            data-dimmed={focus.active !== null && focus.active !== index ? true : undefined}
          >
            <Bars segments={segmentsAt(series, index)} scale={scale} />
          </span>
        ))}
      </div>
      {focus.active === null ? null : (
        <ChartTooltip
          title={buckets[focus.active]?.label ?? ''}
          rows={tooltipRowsOf(segmentsAt(series, focus.active))}
          position={centerOf(focus.active, buckets.length)}
        />
      )}
    </PlotFrame>
  );
};

export const StackedBarChart = ({ loading = false, emptyTitle, ...chart }: StackedBarChartProps) => {
  if (loading) return <Skeleton height="18rem" />;
  if (!hasValues(chart.series)) return <EmptyState title={emptyTitle} />;
  return (
    <ChartFigure label={chart.label} keys={keysOf(chart.series)} series={chart.series}>
      <Plot {...chart} />
    </ChartFigure>
  );
};
